import type { CreateSubOrgParams, TurnkeyProviderConfig } from "@turnkey/react-wallet-kit";

import { publicEnv } from "./publicEnv";

// Client-side cap on embedded-wallet accounts per org.
export const MAX_EMBEDDED_WALLET_ACCOUNTS = 5;

// Label Turnkey shows for every passkey we enroll, at signup and on recovery.
export const AGORA_PASSKEY_NAME = "Agora Passkey";

// Seed one EVM account at signup. Solana disabled for now. Exported so the custom signup flow
// passes the same wallet params to completeOtp's createSubOrgParams.
export const defaultCustomWallet = {
  walletName: "Default wallet",
  walletAccounts: [
    {
      addressFormat: "ADDRESS_FORMAT_ETHEREUM",
      curve: "CURVE_SECP256K1",
      path: "m/44'/60'/0'/0/0",
      pathFormat: "PATH_FORMAT_BIP32",
    },
  ],
} satisfies NonNullable<CreateSubOrgParams["customWallet"]>;

const createSuborgParams = {
  emailOtpAuth: { customWallet: defaultCustomWallet },
  passkeyAuth: { customWallet: defaultCustomWallet },
} satisfies NonNullable<TurnkeyProviderConfig["auth"]>["createSuborgParams"];

const auth = {
  autoRefreshSession: true,
  createSuborgParams,
} satisfies TurnkeyProviderConfig["auth"];

// No wallet manager. Only embedded wallets are used (email-OTP / passkey). Both features off means
// createWalletManager is never called, so neither the connector nor stamper constructor runs. Enabling
// either forces the manager to build, but its constructor throws "no wallet interfaces provided" unless a
// native chain or WalletConnect supplies at least one wallet, so an empty-but-present manager is impossible.
// autoRefreshManagedState:false (below) stops the SDK's post-auth refresh from calling into the absent
// manager; TurnkeyUserSync loads the user via refreshUser instead.
const walletConfig = {
  chains: {},
  features: { auth: false, connecting: false },
} satisfies NonNullable<TurnkeyProviderConfig["walletConfig"]>;

// Empty (local dev, or an env without the profile yet) reads as unconfigured, which hides the recovery path.
export function getTurnkeyRecoverySessionProfileId(): string | undefined {
  return publicEnv.NEXT_PUBLIC_TURNKEY_RECOVERY_SESSION_PROFILE_ID || undefined;
}

// Both ids are required: the auth proxy drives OTP and passkey, so an org id alone
// cannot sign anyone in.
export function getTurnkeyConfig(): TurnkeyProviderConfig | null {
  const organizationId = publicEnv.NEXT_PUBLIC_TURNKEY_ORG_ID;
  const authProxyConfigId = publicEnv.NEXT_PUBLIC_AUTH_PROXY_CONFIG_ID;
  if (!organizationId || !authProxyConfigId) return null;

  return {
    // Empty falls back to the SDK's default host; only a non-default per-env host matters.
    apiBaseUrl: publicEnv.NEXT_PUBLIC_TURNKEY_API_BASE_URL || undefined,
    auth,
    authProxyConfigId,
    // Disables auto refresh of users and wallets.
    //  Wallets should NOT be refreshed since no manager is configured
    // User SHOULD be refreshed, as is refreshed via TurnkeyUserSync component.
    autoRefreshManagedState: false,
    organizationId,
    walletConfig,
    ui: {
      authModal: {
        methods: {
          emailOtpAuthEnabled: true,
          passkeyAuthEnabled: true,
        },
      },
      supressMissingStylesError: true, // Disable styles check. Only do this if you are sure that the styles are being applied correctly.
    },
  } satisfies TurnkeyProviderConfig;
}
